import React, { FC, ReactNode } from 'react';

export interface AuthLayoutProps {
    /** Page heading - "Login", "Signup". */
    title: string;
    /** One line under the heading saying what this page is for. */
    subtitle?: string;
    /** The sign-in controls. */
    children: ReactNode;
    /** The "New user? Create an account" line, pinned under the card. */
    footer?: ReactNode;
    className?: string;
}

/**
 * Shared shell for /login and /signup.
 *
 * The controls sit inside a bordered card rather than floating on the page
 * background: on a wide screen a bare 28rem column stranded in the middle of
 * an empty viewport reads as unfinished, and the card also gives the "OR"
 * divider something to cut across.
 *
 * `AuthDivider` paints its label `bg-white dark:bg-neutral-900` to punch a hole
 * in its own rule, so the card surface has to stay those exact colours.
 */
const AuthLayout: FC<AuthLayoutProps> = ({ title, subtitle, children, footer, className = '' }) => (
    <div className={`container ${className}`}>
        <div className="mx-auto w-full max-w-md pb-20 pt-12 md:pt-16">
            <header className="mb-8 text-center">
                <h1 className="text-3xl font-semibold leading-tight text-neutral-900 md:text-4xl dark:text-neutral-100">
                    {title}
                </h1>
                {subtitle && (
                    <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-neutral-500 dark:text-neutral-400">
                        {subtitle}
                    </p>
                )}
            </header>

            <div className="rounded-3xl border border-neutral-200 bg-white p-6 shadow-sm sm:p-8 dark:border-neutral-700 dark:bg-neutral-900">
                <div className="space-y-6">{children}</div>
            </div>

            {footer && (
                <div className="mt-6 text-center text-sm text-neutral-600 dark:text-neutral-300">{footer}</div>
            )}

            <p className="mt-8 flex items-center justify-center gap-1.5 text-xs text-neutral-400 dark:text-neutral-500">
                <svg className="h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path
                        fillRule="evenodd"
                        d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z"
                        clipRule="evenodd"
                    />
                </svg>
                Secured with one-time codes - no password to remember
            </p>
        </div>
    </div>
);

export default AuthLayout;
